import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDots, Users, Clock, PencilSimple, Trash, ListBullets } from "@phosphor-icons/react";
import { formatCurrency, formatDate, formatTimeShort } from "@/lib/format";

export interface CycleSummary {
  id: string;
  name: string;
  description: string;
  price: number;
  maxParticipants: number;
  durationMinutes: number;
  state: "open" | "locked" | "completed" | "cancelled";
  sessionCount: number;
  firstSessionAt: string | null;
  activeEnrollments: number;
  remainingSeats: number;
}

interface Props {
  cycle: CycleSummary;
  onEdit?: () => void;
  onCancel?: () => void;
  onShowParticipants?: () => void;
}

const stateLabels: Record<CycleSummary["state"], { label: string; tone: string }> = {
  open: { label: "Prijave otvorene", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  locked: { label: "U toku", tone: "bg-amber-100 text-amber-700 border-amber-200" },
  completed: { label: "Završeno", tone: "bg-slate-100 text-slate-700 border-slate-200" },
  cancelled: { label: "Otkazano", tone: "bg-rose-100 text-rose-700 border-rose-200" },
};

export function GroupTherapyCard({ cycle, onEdit, onCancel, onShowParticipants }: Props) {
  const stateInfo = stateLabels[cycle.state];
  return (
    <Card className="border-[#ece8df]">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-lg" style={{ fontFamily: "var(--font-heading)" }}>
            {cycle.name}
          </CardTitle>
          <span
            className={`mt-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${stateInfo.tone}`}
          >
            {stateInfo.label}
          </span>
        </div>
        {cycle.state === "open" && (onEdit || onCancel) && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <Button size="sm" variant="ghost" onClick={onEdit}>
                <PencilSimple className="size-4" />
              </Button>
            )}
            {onCancel && (
              <Button size="sm" variant="ghost" onClick={onCancel}>
                <Trash className="size-4" />
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="line-clamp-2 text-sm text-muted-foreground">{cycle.description}</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2 text-foreground">
            <CalendarDots className="size-4 text-muted-foreground" />
            {cycle.firstSessionAt
              ? `${formatDate(cycle.firstSessionAt)} u ${formatTimeShort(cycle.firstSessionAt)}`
              : "Bez termina"}
          </div>
          <div className="flex items-center gap-2 text-foreground">
            <Clock className="size-4 text-muted-foreground" />
            {cycle.sessionCount} {cycle.sessionCount === 1 ? "sesija" : "sesija"} · {cycle.durationMinutes} min
          </div>
          <div className="flex items-center gap-2 text-foreground">
            <Users className="size-4 text-muted-foreground" />
            {cycle.activeEnrollments} / {cycle.maxParticipants} učesnika
          </div>
          <div className="flex items-center gap-2 font-medium text-foreground">
            {formatCurrency(cycle.price)}
          </div>
        </div>
        {onShowParticipants && (
          <Button size="sm" variant="outline" onClick={onShowParticipants}>
            <ListBullets className="size-4" /> Učesnici i linkovi
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
